
import PropTypes from 'prop-types'
import { Button, Grid, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import CustomTabs from './custom-tabs'
import SingleSelect from './single-select'
import { useNavigate } from 'react-router-dom'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'

const PageHeader = ({
  subTitleStyles,
  config,
  className,
  breadcrumbs,
  breadcrumbsLink,
  title,
  subtitle,
  tabsConfig,
  loading,
  options = [],
  disabledButtons,

  primaryButton,
  secondaryButton,
  dropdown,
  ...rest
}) => {

  const navigate = useNavigate()

  const mobileView = useMediaQuery('(max-width: 600px)')

  // custom options
  const titleWordWrap = options.includes('titleWordWrap')

  const onNavigate = (link) => link && navigate(link)

  return (
    <div
      style={{
        padding: '16px 0px 32px 0px',
        ...className
      }}
      {...rest}>

      {tabsConfig ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography component="h1" variant="h3" align="center">
              {title || ''}
            </Typography>
          </Grid>
          <Grid item xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}>
            {!loading && (
              <CustomTabs tabsConfig={tabsConfig} disabled={loading} />
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid alignItems="flex-end" container justify="space-between" spacing={3} style={!primaryButton && !secondaryButton ? { paddingTop: 18 } : {}}>
          <Grid item>
            {breadcrumbs && breadcrumbsLink && (
              <Button
                sx={{
                  color: '#546e7a',
                  fontSize: '11px',
                  fontWeight: '500',
                  lineHeight: '13px',
                  letterSpacing: '0.33px',
                  '&:hover': {
                    background: 'none',
                    textDecoration: 'underline',
                  }
                }}
                size="small" startIcon={<KeyboardBackspaceIcon />} onClick={() => onNavigate(breadcrumbsLink)}>
                Back to {breadcrumbs}
              </Button>
            )}
            <Typography
              sx={{
                maxWidth: 450,
                ...(titleWordWrap && {
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                })
              }}
              component="h1" variant="h3">
              {title}
            </Typography>
            {subtitle && (
              <Typography component="h6" variant="h6" align="left" style={ subTitleStyles ? subTitleStyles : { margin: '5px 0 -30px 0' }}>
                {subtitle}
              </Typography>
            )}
          </Grid>

          <Grid
            sx={{
              marginTop: 2,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              '@media (max-width: 600px)': { width: '100%', margin: '-24px 0px -8px 0px' }
            }}
            item>

            {/* Dropdown */}
            {dropdown && !dropdown.hide && (
              <div
                style={{
                  backgroundColor: '#FFF',
                  borderRadius: 10,
                  width: 300,
                  '@media (max-width: 600px)': { width: '100%', margin: '40px 0px 16px 0px' },
                  '@media (max-width: 0px)': { marginBottom: 0 }
                }}>
                <SingleSelect
                  noNone tight
                  id="pageHeaderDropdown"
                  label={dropdown.label}
                  options={dropdown.options}
                  value={dropdown.value}
                  onChange={dropdown.onChange}
                  disabled={disabledButtons || dropdown.disable}/>
              </div>
            )}

            {/* Secondary Button (Left) */}
            {secondaryButton && secondaryButton.action && !secondaryButton.hide && (
              <Button
                sx={{
                  marginLeft: 2,
                  '@media (max-width: 0px)': { margin: '16px 0px 0px 0px' }
                }}
                fullWidth={mobileView}
                disabled={disabledButtons || secondaryButton.disable}
                id="secondary-action"
                data-cy="secondary-action"
                color={secondaryButton.color || 'secondary'}
                variant="contained"
                onClick={secondaryButton.action}>
                {secondaryButton.label}
              </Button>
            )}

            {/* Primary Button (Right) */}
            {primaryButton && primaryButton.action && !primaryButton.hide && (
              <Button
                sx={{
                  marginLeft: 2,
                  '@media (max-width: 0px)': { margin: '16px 0px 0px 0px' }
                }}
                fullWidth={mobileView}
                disabled={disabledButtons || primaryButton.disable}
                id="primary-action"
                data-cy="primary-action"
                color={primaryButton.color || 'primary'}
                variant="contained"
                onClick={primaryButton.action}>
                {primaryButton.label}
              </Button>
            )}
          </Grid>
        </Grid>
      )}

    </div>
  )
}

PageHeader.propTypes = {
  subTitleStyles: PropTypes.any,
  config: PropTypes.any,
  className: PropTypes.string,
  breadcrumbs: PropTypes.string,
  breadcrumbsLink: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  tabsConfig: PropTypes.array,
  loading: PropTypes.bool,
  options: PropTypes.array,
  disabledButtons: PropTypes.bool,

  primaryButton: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
    hide: PropTypes.bool,
    disable: PropTypes.bool,
  }),

  secondaryButton: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
    color: PropTypes.string,
    hide: PropTypes.bool,
    disable: PropTypes.bool,
  }),

  dropdown: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    options: PropTypes.array,
    onChange: PropTypes.func,
    hide: PropTypes.bool,
    disable: PropTypes.bool,
  }),
}

export default PageHeader
